import { useEffect, useState } from "react";

import { Menu, User } from "lucide-react";

interface HeaderLink {
  label: string;
  href?: string;
}
interface Crumb {
  label: string;
  href?: string;
}

export interface SignatureProfileHeaderData {
  logoSrc: string;
  brandName: string;
  nav: HeaderLink[];
  breadcrumbs: Crumb[];
  accountUrl?: string;
}

export const SignatureProfileHeader: React.FC<{
  data: SignatureProfileHeaderData;
}> = ({ data }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`${
        scrolled ? "bg-white/70 backdrop-blur" : "bg-transparent"
      } fixed top-0 w-full z-50 transition-all duration-300 py-4`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center gap-8">
          <a className="flex items-center gap-2" href="/">
            <img alt="Artify" className="h-8 w-auto" src={data.logoSrc} />
            <span className="font-playfair text-xl font-light">
              {data.brandName}
            </span>
          </a>
          <nav className="hidden md:block">
            <ul className="flex space-x-6">
              {data.nav
                .filter((item) => item.href && item.href !== "#")
                .map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="hover:text-artist-accent transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
            </ul>
          </nav>
        </div>
        {data.accountUrl && data.accountUrl !== "#" && (
          <div className="hidden md:flex items-center gap-4">
            <a
              href={data.accountUrl}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3 rounded-full"
            >
              <User className="mr-2" size={20} />
              Account
            </a>
          </div>
        )}
        <div className="md:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-10 w-10"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg z-50">
          <nav className="container mx-auto px-4 py-4">
            <ul className="space-y-3">
              {data.nav
                .filter((item) => item.href && item.href !== "#")
                .map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="block py-2 hover:text-artist-accent transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              {data.accountUrl && data.accountUrl !== "#" && (
                <li>
                  <a
                    href={data.accountUrl}
                    className="block py-2 hover:text-artist-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Account
                  </a>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}

      {!scrolled && (
        <div className="container mx-auto px-4 pt-2 opacity-100 transition-opacity duration-300">
          <div className="text-sm text-muted-foreground">
            <ul className="flex flex-wrap">
              {data.breadcrumbs.map((c) => (
                <li key={c.label} className="breadcrumb-item">
                  {c.href && c.href !== "#" ? (
                    <a href={c.href} className="hover:text-artist-accent">
                      {c.label}
                    </a>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
};
